import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { readEdgeError } from "@/lib/edgeError";
import { useOutlet } from "@/contexts/OutletContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useChannelConnection } from "@/hooks/useChannelConnection";
import {
  plans,
  isStripeTestMode,
  CYCLE_UNIT,
  planChargeForCycle,
  planPriceIdForCycle,
  type BillingCycle,
} from "@/config/stripePlans";
import { toast } from "sonner";
import {
  CircleFadingArrowUp,
  UserPlus,
  UserX,
  Send,
  Play,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Zap,
  GitBranch,
  Brain,
  Sparkles,
  Info,
  Clock,
  CheckCircle2,
  Lock,
  Plus,
  ShieldAlert,
  CircleDot,
  Link2,
  List,
  History,
  AlertCircle,
  Workflow,
  Search,
  X,
  Pencil,
  Archive,
  Bot,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import WorkflowCapabilitySettings from "@/components/admin_dashboard/WorkflowCapabilitySettings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  type BlockKind,
  type NodeType,
  type DisplayStep,
  type WorkflowDefinition,
  NODE_TYPES,
  toDisplaySteps,
  workflowRequiresApproval,
} from "@/lib/workflows/schema";
import {
  ATTENDANCE_CONFIRMATION,
  IDLE_LEAD_WINBACK,
  NEW_LEAD_QUALIFICATION,
  NO_SHOW_RECOVERY,
  TRIAL_TO_PAID,
  CRISIS_OUTREACH_PROTECTION,
  COMPLAINT_RECOVERY,
} from "@/lib/workflows/definitions";
import {
  WORKFLOW_RECIPES,
} from "@/lib/workflows/recipes";
import {
  DEFAULT_WORKFLOW_CARD_COPY,
  DEFAULT_WORKFLOW_CATALOG,
  getWorkflowLifecycleBucket,
  isWorkflowTemplateVisible,
  TEMPLATE_CARD_COPY,
} from "@/lib/workflows/catalog-ui";
import WorkflowBuilder from "@/components/admin_dashboard/workflow_builder/WorkflowBuilder";
import CopilotPanel from "@/components/admin_dashboard/workflow_builder/copilot/CopilotPanel";
import { planHasFeature } from "@/lib/planEntitlements";
import OutreachHolds from "@/components/admin_dashboard/workflow_builder/OutreachHolds";
import {
  FacebookIcon,
  GmailIcon,
  InstagramIcon,
  MessengerIcon,
  TelegramIcon,
  WhatsAppIcon,
} from "@/components/admin_dashboard/BrandIcons";

interface AutomationsProps {
  onNavigateToSection?: (section: string, extraParams?: Record<string, string>) => void;
  onBuilderNameChange?: (name: string | null) => void;
  onDetailChange?: (workflow: { id: string; name: string } | null) => void;
}

// Execution history remains stored for internal auditing, but is intentionally
// hidden from every workflow surface in the admin UI.
const SHOW_WORKFLOW_RUN_LOGS = false;

const WorkflowCatalogLoading = () => (
  <div
    className="space-y-8"
    data-testid="workflow-catalog-loading"
    role="status"
    aria-live="polite"
    aria-label="Loading workflow activity"
  >
    {[0, 1].map((section) => (
      <section key={section} className="space-y-3">
        <div className="flex items-end justify-between gap-3 border-b border-border pb-3">
          <div className="space-y-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-4 w-72 max-w-[70vw]" />
          </div>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((card) => (
            <Skeleton key={card} className="h-[156px] rounded-lg" />
          ))}
        </div>
      </section>
    ))}
    <span className="sr-only">Loading your workflows…</span>
  </div>
);

// Diagram cards now come from the workflow schema (src/lib/workflows), so the
// "How it works" picture is generated from the same object the engine runs.
// Workflows not yet migrated to the graph model still author these inline.
type StepKind = BlockKind;
type AutomationStep = DisplayStep & {
  /** Frontend-only branch direction. The default condition continues on Yes. */
  continueOn?: "yes" | "no";
};

const CONVERT_NEW_LEAD_NAME = "Convert a new lead";
const CONVERT_NEW_LEAD_DESCRIPTION =
  "Understand new enquiries and keep promising leads moving towards becoming customers.";
const REENGAGE_NEW_LEAD_NAME = "Re-engage a new lead that ghosts";
const MAX_ROUND_OPTIONS = ["1", "2", "3", "5", "Indefinitely"];

/**
 * The capability diagram starts with the canonical backend definition and adds
 * only frontend presentation around its lead-conversion decision.
 */
const CONVERT_NEW_LEAD_STEPS: AutomationStep[] = (() => {
  let rewroteEligibility = false;
  const backendSteps: AutomationStep[] = toDisplaySteps(NEW_LEAD_QUALIFICATION).map((step) => {
    // This workflow always targets new senders. It is part of the capability's
    // fixed definition, so the frontend does not present it as a user setting.
    const params = step.kind === "trigger"
      ? step.params?.filter((param) => param.label !== "New enquiries only")
      : step.params;

    if (step.kind === "decision" && step.label === "Qualify the enquiry") {
      return {
        ...step,
        label: "Convert lead",
        description: "Dibs understands what the lead needs and decides how to respond.",
        params: params?.length ? params : undefined,
      };
    }

    if (step.kind === "execution" && step.label === "Add tag") {
      return {
        ...step,
        label: "Mark as a new enquiry",
        description: "Dibs marks the contact as a new enquiry so you can find them later.",
        params: params?.length ? params : undefined,
      };
    }

    if (rewroteEligibility || step.kind !== "condition") {
      return {
        ...step,
        params: params?.map((param) => ({
          ...param,
          values: param.values.map((value) => value
            .replace("Qualify the enquiry", "Convert lead")
            .replace("has been qualified", "is ready for review")),
        })).filter((param) => param.values.length) || undefined,
      };
    }
    rewroteEligibility = true;
    return {
      ...step,
      params: params?.length ? params : undefined,
      description: "Has the lead opted out of marketing messages?",
      // The backend skips opted-out leads. Phrase that same rule directly, so
      // Yes ends the path and No continues the conversion workflow.
      exceptions: ["They have opted out of marketing messages"],
      continueOn: "no",
    };
  });

  // Frontend-only concept: present lead conversion as a bounded conversation
  // loop without changing the canonical backend workflow definition.
  const convertIndex = backendSteps.findIndex((step) => step.label === "Convert lead");
  if (convertIndex !== -1) {
    const convertStep = backendSteps[convertIndex];
    backendSteps.splice(
      convertIndex,
      1,
      {
        kind: "loop",
        label: "While the lead is still considering",
        description: "Dibs keeps understanding and responding while the lead is deciding whether to book.",
        params: [{ label: "Max rounds", values: ["3"] }],
        stopRules: ["The lead books, opts out, declines, or needs a person"],
        loopBodyLength: 2,
      },
      {
        ...convertStep,
        params: [{ label: "Goal", values: ["Turn the enquiry into a booking"] }],
        loopId: "lead-conversion-conversation",
      },
      {
        kind: "execution",
        label: "Reply to the lead",
        description: "Dibs replies to the lead and guides them towards booking.",
        loopId: "lead-conversion-conversation",
      },
    );
  }

  return backendSteps;
})();

// Backend automation keys. MUST match
// supabase/functions/_shared/automations/registry.ts (AUTOMATION_KEYS).
type AutomationKey =
  | "no_show_recovery"
  | "attendance_confirmation"
  | "trial_to_paid"
  | "conversation_follow_up"
  | "crisis_management"
  | "complaint_recovery";

interface AutomationDef {
  id: string;
  definition: WorkflowDefinition;
  /** The backend automation_settings / automation_runs key. */
  key: AutomationKey;
  name: string;
  description: string;
  icon: LucideIcon;
  /** Plain-English trigger summary shown on the card. */
  trigger: string;
  steps: AutomationStep[];
  /**
   * Optional hand-crafted prose for the Simplified view. When present, it
   * replaces the auto-generated summary. Each inner array is one visual
   * paragraph; entries within it flow inline with their block icon. Use a
   * single inner array for one continuous paragraph, multiple inner arrays
   * for intentionally separated paragraphs.
   */
  prose?: { kind: StepKind; text: string }[][];
  /** Optional deep-link into the activity view that shows this automation's output. */
  activitySection?: string;
  activityLabel?: string;
  /** Set when the automation routes through the approval queue before acting. */
  requiresApproval?: boolean;
  /**
   * High-volume automations don't write per-event run rows (e.g. auto-reply
   * logs to Conversations instead). For these we hide run counters/history and
   * point the owner to the live activity view.
   */
  noRunLog?: boolean;
  /**
   * Opt-IN automations default OFF when no settings row exists (mirrors the
   * backend isAutomationOptedIn helper). Everything else defaults ON.
   */
  defaultOff?: boolean;
}

// A single logged run, as stored in automation_runs.
interface AutomationRun {
  id: string;
  automation_key: string;
  status: string;
  detail: string | null;
  item_count: number;
  created_at: string;
}

// An owner-built workflow, as stored in workflow_definitions.
interface CustomWorkflowRow {
  id: string;
  key: string;
  name: string;
  description: string;
  status: "draft" | "published" | "archived";
  definition: WorkflowDefinition;
  version: number;
  source_template_id: string | null;
  updated_at: string;
}

// One interpreter run with its event trail (workflow_runs + workflow_run_events).
interface CustomRunRow {
  id: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  error: string | null;
  current_node_id: string | null;
  subject: Record<string, unknown>;
  events: Array<{
    kind: string;
    summary: string;
    created_at: string;
    node_id: string | null;
  }>;
}

type CatalogSource = "ready" | "template" | "custom";
type CatalogBusinessType = "health" | "hospitality";
type CatalogTab = "capabilities" | "my" | "templates";

interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  sectionId: string;
  source: CatalogSource;
  businessTypes: CatalogBusinessType[];
  automation?: AutomationDef;
  recipeId?: string;
  definition?: WorkflowDefinition;
  previewSteps?: AutomationStep[];
  custom?: CustomWorkflowRow;
}

const CATALOG_SECTIONS = [
  {
    id: "growth",
    title: "Grow & follow up",
    description: "Turn promising enquiries and first visits into lasting customer relationships.",
  },
  {
    id: "bookings",
    title: "Protect bookings",
    description: "Reduce empty slots and help customers get back on schedule.",
  },
  {
    id: "care",
    title: "Customer care",
    description: "Follow up thoughtfully when an experience needs a human touch.",
  },
  {
    id: "learn",
    title: "Feedback",
    description: "Learn what customers enjoyed and where your team can improve.",
  },
  {
    id: "recover",
    title: "Win customers back",
    description: "Offer an easy next step after a cancellation or resolved issue.",
  },
  {
    id: "custom",
    title: "Custom workflows",
    description: "Workflows created and tailored by your team.",
  },
] as const;

type CatalogObjective = Exclude<(typeof CATALOG_SECTIONS)[number]["id"], "custom">;
const CATALOG_OBJECTIVES = CATALOG_SECTIONS.filter(
  (section): section is (typeof CATALOG_SECTIONS)[number] & { id: CatalogObjective } => section.id !== "custom",
);

// Human-friendly relative timestamp.
function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "Yesterday";
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString();
}

// Registry. Mirrors the backend workflows in
// supabase/functions/_shared/workflows/. Each maps to a backend key that
// drives the enable/disable toggle and run history.

const AUTOMATIONS: AutomationDef[] = [
  {
    // Steps, name, description and approval flag are derived from the graph-model
    // definition. The diagram renders the same object the engine would run.
    id: TRIAL_TO_PAID.id,
    definition: TRIAL_TO_PAID,
    key: TRIAL_TO_PAID.key as AutomationKey,
    name: TRIAL_TO_PAID.name,
    description: TRIAL_TO_PAID.description,
    icon: CircleFadingArrowUp,
    trigger: "When you mark a customer's first visit as completed",
    activitySection: "campaigns",
    activityLabel: "View campaigns",
    requiresApproval: workflowRequiresApproval(TRIAL_TO_PAID),
    steps: toDisplaySteps(TRIAL_TO_PAID),
    prose: [[
      { kind: "trigger",   text: "When you mark a customer's first booking as completed (their trial or first visit)," },
      { kind: "execution", text: "Dibs invites them in their existing chat, or by booking email if they have never messaged you." },
    ]],
  },
  {
    id: ATTENDANCE_CONFIRMATION.id,
    definition: ATTENDANCE_CONFIRMATION,
    key: ATTENDANCE_CONFIRMATION.key as AutomationKey,
    name: ATTENDANCE_CONFIRMATION.name,
    description: ATTENDANCE_CONFIRMATION.description,
    icon: CheckCircle2,
    trigger: "48 hours before a confirmed booking",
    activitySection: "conversations",
    activityLabel: "View conversations",
    requiresApproval: workflowRequiresApproval(ATTENDANCE_CONFIRMATION),
    steps: toDisplaySteps(ATTENDANCE_CONFIRMATION),
    prose: [[
      { kind: "trigger", text: "48 hours before a booking," },
      { kind: "execution", text: "Dibs asks the customer to reply CONFIRM or CANCEL." },
      { kind: "delay", text: "If there is no explicit confirmation, it sends one final reminder." },
      { kind: "execution", text: "The booking is never released automatically; Dibs alerts your team to review it." },
    ]],
  },
  {
    id: NO_SHOW_RECOVERY.id,
    definition: NO_SHOW_RECOVERY,
    key: NO_SHOW_RECOVERY.key as AutomationKey,
    name: NO_SHOW_RECOVERY.name,
    description: NO_SHOW_RECOVERY.description,
    icon: UserX,
    trigger: "When a booking is marked as a no-show",
    activitySection: "conversations",
    activityLabel: "View conversations",
    requiresApproval: workflowRequiresApproval(NO_SHOW_RECOVERY),
    steps: toDisplaySteps(NO_SHOW_RECOVERY),
    prose: [[
      { kind: "trigger", text: "When your team marks a customer as a no-show," },
      { kind: "delay", text: "Dibs waits 24 hours to avoid piling onto appointment messages," },
      { kind: "execution", text: "Dibs sends one respectful chat or booking-email message offering help to book another suitable time." },
    ]],
  },
  {
    id: IDLE_LEAD_WINBACK.id,
    definition: IDLE_LEAD_WINBACK,
    key: IDLE_LEAD_WINBACK.key as AutomationKey,
    name: REENGAGE_NEW_LEAD_NAME,
    description: IDLE_LEAD_WINBACK.description,
    icon: Send,
    trigger: "When a new lead replies, then goes quiet",
    activitySection: "conversations",
    activityLabel: "View conversations",
    defaultOff: true,
    requiresApproval: workflowRequiresApproval(IDLE_LEAD_WINBACK),
    steps: toDisplaySteps(IDLE_LEAD_WINBACK),
    prose: [[
      { kind: "trigger", text: "When a new lead messages and you reply but they go quiet, this workflow steps in." },
      { kind: "delay", text: "Dibs waits 24 hours to give the lead a chance to respond on their own." },
      { kind: "condition", text: "If they're still unresponsive — and haven't opted out or been contacted recently by another workflow — Dibs continues." },
      { kind: "decision", text: "The AI drafts a short, friendly follow-up tailored to the conversation," },
      { kind: "execution", text: "and sends it to the lead on WhatsApp." },
    ]],
  },
  {
    id: NEW_LEAD_QUALIFICATION.id,
    definition: NEW_LEAD_QUALIFICATION,
    key: NEW_LEAD_QUALIFICATION.key as AutomationKey,
    name: CONVERT_NEW_LEAD_NAME,
    description: CONVERT_NEW_LEAD_DESCRIPTION,
    icon: UserPlus,
    trigger: "When someone messages you for the first time",
    activitySection: "conversations",
    activityLabel: "View conversations",
    defaultOff: true,
    requiresApproval: workflowRequiresApproval(NEW_LEAD_QUALIFICATION),
    steps: CONVERT_NEW_LEAD_STEPS,
    prose: [[
      { kind: "trigger", text: "When a number that has never contacted you before sends you a message," },
      { kind: "condition", text: "and they have not opted out of marketing messages," },
      { kind: "decision", text: "the AI reads what they wrote and works out what they want and how ready they are to book," },
      { kind: "execution", text: "tags them as a new enquiry," },
      { kind: "execution", text: "and emails you a short summary so you can chase the ones worth your time." },
    ]],
  },
  {
    id: COMPLAINT_RECOVERY.id,
    definition: COMPLAINT_RECOVERY,
    key: COMPLAINT_RECOVERY.key as AutomationKey,
    name: COMPLAINT_RECOVERY.name,
    description: COMPLAINT_RECOVERY.description,
    icon: ShieldAlert,
    trigger: "When a complaint arrives from a connected review or email source",
    activitySection: "crisis",
    activityLabel: "Open recovery inbox",
    requiresApproval: workflowRequiresApproval(COMPLAINT_RECOVERY),
    steps: toDisplaySteps(COMPLAINT_RECOVERY),
    prose: [[
      { kind: "trigger", text: "When a complaint arrives from Google Reviews or email," },
      { kind: "execution", text: "Dibs puts it into one trackable recovery case with a clear owner and deadlines," },
      { kind: "execution", text: "then alerts your team." },
      { kind: "decision", text: "Dibs AI drafts a careful, specific response," },
      { kind: "execution", text: "but Google or email receives nothing until a person approves the exact wording." },
      { kind: "condition", text: "If your optional recovery-credit policy safely applies," },
      { kind: "execution", text: "a separate approval is required before Dibs privately emails a one-use code." },
    ]],
  },
  {
    id: CRISIS_OUTREACH_PROTECTION.id,
    definition: CRISIS_OUTREACH_PROTECTION,
    key: CRISIS_OUTREACH_PROTECTION.key as AutomationKey,
    name: CRISIS_OUTREACH_PROTECTION.name,
    description: CRISIS_OUTREACH_PROTECTION.description,
    icon: ShieldAlert,
    trigger: "When a sensitive customer case is opened",
    activitySection: "crisis",
    activityLabel: "Open crisis inbox",
    defaultOff: true,
    requiresApproval: workflowRequiresApproval(CRISIS_OUTREACH_PROTECTION),
    steps: toDisplaySteps(CRISIS_OUTREACH_PROTECTION),
    prose: [[
      { kind: "trigger", text: "When Dibs AI or your team opens a sensitive complaint, clinical, safety or privacy case," },
      { kind: "condition", text: "Dibs checks whether ordinary outreach should stop," },
      { kind: "execution", text: "pauses proactive sales and follow-ups, then marks the profile for human care until your team records the resolution." },
    ]],
  },
];

/** Display name for a workflow id. Used by the breadcrumb in Admin. */
export function getWorkflowName(id: string): string | null {
  return AUTOMATIONS.find((a) => a.id === id)?.name ?? null;
}

// Step diagram (read-only)

const STEP_STYLES: Record<StepKind, { icon: LucideIcon; chip: string; label: string; tooltip?: string }> = {
  trigger: { icon: Zap, chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Trigger", tooltip: "The event that starts the workflow." },
  condition: { icon: GitBranch, chip: "bg-blue-50 text-blue-700 border-blue-200", label: "Control", tooltip: "Choose a path with an if-else condition or repeat steps with a while loop." },
  decision: { icon: Brain, chip: "bg-violet-50 text-violet-700 border-violet-200", label: "Decision", tooltip: "Where the AI uses judgment to tailor the outcome." },
  execution: { icon: Play, chip: "bg-primary/10 text-primary border-primary/20", label: "Execution", tooltip: "The action the workflow carries out." },
  delay: { icon: Clock, chip: "bg-muted text-muted-foreground border-border", label: "Wait", tooltip: "A time gap — waits a fixed duration or until an event fires." },
  // The second Control block, so it shares the Control colour.
  loop: { icon: GitBranch, chip: "bg-blue-50 text-blue-700 border-blue-200", label: "While loop", tooltip: "Repeats the blocks inside it until a stop rule matches or the rounds run out." },
  action: { icon: ArrowRight, chip: "bg-muted text-muted-foreground border-border", label: "Action" },
};

// The canonical authoring primitives, shown as a palette above the canvas to
// signal that a workflow is assembled from typed blocks.
const PALETTE_KINDS: StepKind[] = ["trigger", "condition", "decision", "execution", "delay"];

const canvasBlockTypeLabel = (kind: StepKind) => {
  if (kind === "condition") return "If-else condition";
  if (kind === "loop") return "While loop";
  return STEP_STYLES[kind].label;
};

// A node port. The handle where a connector attaches. Purely visual for now;
// in the builder these become the drag targets for wiring blocks together.
const Handle = ({ position }: { position: "top" | "bottom" }) => (
  <span
    className={`absolute left-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-border bg-background ${
      position === "top" ? "-top-[5px]" : "-bottom-[5px]"
    }`}
  />
);

// Vertical connector between nodes, with an optional "insert a block here"
// affordance that foreshadows editing the flow.
const Connector = ({ insert = false }: { insert?: boolean }) => (
  <div className="flex justify-center">
    <div className="relative flex h-10 w-full items-center justify-center">
      <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border" />
      {insert ? (
        <span className="z-10 flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-border bg-background text-muted-foreground/60">
          <Plus className="h-3 w-3" />
        </span>
      ) : (
        <ChevronDown
          className="absolute bottom-0 left-1/2 h-4 w-4 -translate-x-1/2 text-muted-foreground/40"
          strokeWidth={2}
        />
      )}
    </div>
  </div>
);

const LabeledConnector = ({ label, solid = false }: { label: string; solid?: boolean }) => (
  <div className="flex justify-center">
    <div className="relative h-14 w-full">
      <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border" />
      <span className={`absolute left-1/2 top-7 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        solid ? "bg-muted-foreground text-background" : "bg-muted text-muted-foreground"
      }`}>
        {label}
      </span>
      <ChevronDown
        className="absolute bottom-0 left-1/2 h-4 w-4 -translate-x-1/2 text-muted-foreground/40"
        strokeWidth={2}
      />
    </div>
  </div>
);

// Entry / exit marker bookending the canvas.
const FlowCap = ({ label }: { label: string }) => (
  <div className="flex justify-center">
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-[11px] font-medium text-muted-foreground">
      <CircleDot className="h-3 w-3" />
      {label}
    </span>
  </div>
);

// A condition is one control block followed by explicit Yes and No branches.
const ConditionCard = ({
  step,
  onEdit,
  isSelected = false,
}: {
  step: AutomationStep;
  onEdit?: (step: AutomationStep) => void;
  isSelected?: boolean;
}) => {
  const style = STEP_STYLES.condition;
  const StepIcon = style.icon;
  const description = step.description ?? step.label;
  const editProps = onEdit
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": "Edit settings for If-else condition",
        onClick: () => onEdit(step),
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onEdit(step);
          }
        },
      }
    : {};

  return (
    <div
      {...editProps}
      className={`relative rounded-2xl border bg-card shadow-sm ${isSelected ? "border-primary" : "border-border"} ${
        onEdit ? "cursor-pointer transition-colors hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" : ""
      }`}
    >
      <Handle position="top" />
      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${style.chip}`}>
            <StepIcon className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-xs font-semibold leading-snug text-foreground">If-else condition</span>
        </div>
      </div>
      <div className="border-t border-border/70" />
      <p className="line-clamp-2 px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
      <Handle position="bottom" />
    </div>
  );
};

const conditionContinueOn = (step: AutomationStep): "yes" | "no" => {
  if (step.continueOn) return step.continueOn;
  // Keep previously edited in-memory capability steps correct across hot
  // reloads, before the frontend-only continueOn hint existed.
  const conditionCopy = `${step.description ?? ""} ${(step.exceptions ?? []).join(" ")}`;
  return /opted out of marketing messages/i.test(conditionCopy) ? "no" : "yes";
};

const BranchSplit = ({
  conditionStep,
  continueBranch,
  onEditStep,
  selectedStep,
  conditionColumn = "left",
}: {
  conditionStep: AutomationStep;
  continueBranch: ReactNode;
  onEditStep?: (step: AutomationStep) => void;
  selectedStep?: AutomationStep | null;
  conditionColumn?: "left" | "right";
}) => {
  const continueOn = conditionContinueOn(conditionStep);
  const conditionAlignment = conditionColumn === "left" ? "" : "ml-auto";
  const conditionConnectorLeft = conditionColumn === "left" ? "left-[192px]" : "right-[192px]";

  return (
  <div className="w-full">
    <div className={`flex w-[384px] flex-col items-stretch ${conditionAlignment}`}>
      <Connector />
      <ConditionCard
        step={conditionStep}
        onEdit={onEditStep}
        isSelected={selectedStep === conditionStep}
      />
    </div>

    <div className="relative h-20 w-full">
      <div className={`absolute ${conditionConnectorLeft} top-0 h-8 w-px bg-border`} />
      <div className="absolute left-[192px] right-[192px] top-8 h-px bg-border" />
      <div className="absolute bottom-0 left-[192px] top-8 w-px bg-border" />
      <div className="absolute bottom-0 right-[192px] top-8 w-px bg-border" />
      <span
        aria-label={`Yes branch ${continueOn === "yes" ? "continues" : "ends"} workflow`}
        className="absolute left-[192px] top-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background"
      >
        Yes
      </span>
      <span
        aria-label={`No branch ${continueOn === "no" ? "continues" : "ends"} workflow`}
        className="absolute right-[192px] top-8 translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background"
      >
        No
      </span>
      <ChevronDown
        data-testid="yes-branch-arrow"
        className="absolute bottom-0 left-[192px] h-4 w-4 -translate-x-1/2 text-muted-foreground/40"
        strokeWidth={2}
      />
      <ChevronDown
        data-testid="no-branch-arrow"
        className="absolute bottom-0 right-[192px] h-4 w-4 translate-x-1/2 text-muted-foreground/40"
        strokeWidth={2}
      />
    </div>

    <div className="grid grid-cols-2 items-start gap-6">
      <div className="w-[384px]" role="group" aria-label="Yes branch">
        {continueOn === "yes" ? continueBranch : <FlowCap label="Workflow ends" />}
      </div>
      <div className="w-[384px]" role="group" aria-label="No branch">
        {continueOn === "no" ? continueBranch : <FlowCap label="Workflow ends" />}
      </div>
    </div>
  </div>
  );
};

// A single block on the canvas: icon + fixed block type, with the editable
// natural-language description below it. The active block gets the Dibs-red
// border; AI involvement is already communicated by its badge.
const NodeCard = ({
  step,
  onEdit,
  isSelected = false,
}: {
  step: AutomationStep;
  onEdit?: (step: AutomationStep) => void;
  isSelected?: boolean;
}) => {
  const style = STEP_STYLES[step.kind];
  const StepIcon = style.icon;
  // AI involvement is declared on the block type in the registry, not hardcoded.
  const usesAI = NODE_TYPES[step.kind as NodeType]?.usesAI === true;
  const aiInvolved = usesAI;
  const description = step.description ?? step.label;
  return (
    <div
      role={onEdit ? "button" : undefined}
      tabIndex={onEdit ? 0 : undefined}
      aria-label={onEdit ? `Edit settings for ${step.label}` : undefined}
      onClick={onEdit ? () => onEdit(step) : undefined}
      onKeyDown={onEdit ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onEdit(step);
        }
      } : undefined}
      className={`relative rounded-2xl border bg-card shadow-sm ${isSelected ? "border-primary" : "border-border"} ${
        onEdit ? "cursor-pointer transition-colors hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" : ""
      }`}
    >
      <Handle position="top" />
      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${style.chip}`}>
            <StepIcon className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-xs font-semibold leading-snug text-foreground">{canvasBlockTypeLabel(step.kind)}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {aiInvolved && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary">
              <Sparkles className="h-3 w-3" />
              Powered by Dibs AI
            </span>
          )}
        </div>
      </div>
      <div className="border-t border-border/70" />
      <p className="line-clamp-2 px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
      <Handle position="bottom" />
    </div>
  );
};

// Renders a linear run of blocks (node → connector → node …). Loops use
// labelled Repeat and Exit paths so their control flow reads like a branch.
const LinearSteps = ({
  steps,
  onEditStep,
  selectedStep,
  showInsertAffordances = true,
}: {
  steps: AutomationStep[];
  onEditStep?: (step: AutomationStep) => void;
  selectedStep?: AutomationStep | null;
  showInsertAffordances?: boolean;
}) => {
  const items: ReactNode[] = [];
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const isLast = (index: number) => index >= steps.length - 1;
    if (step.kind === "loop") {
      const body = steps.slice(i + 1, i + 1 + (step.loopBodyLength ?? 0));
      const end = i + body.length;
      items.push(
        <div key={i} className="flex flex-col items-stretch">
          <NodeCard step={step} onEdit={onEditStep} isSelected={selectedStep === step} />
          <LabeledConnector label="Repeat" solid />
          <div
            data-testid="while-loop-group"
            className="rounded-2xl border border-border/70 bg-muted/30 p-3"
          >
            <div>
              {body.map((inner, j) => (
                <div key={j} className="flex flex-col items-stretch">
                  <NodeCard step={inner} onEdit={onEditStep} isSelected={selectedStep === inner} />
                  {j < body.length - 1 && <Connector insert={showInsertAffordances} />}
                </div>
              ))}
            </div>
          </div>
          {!isLast(end) && <LabeledConnector label="Exit" solid />}
        </div>,
      );
      i = end;
      continue;
    }
    items.push(
      <div key={i} className="flex flex-col items-stretch">
        <NodeCard step={step} onEdit={onEditStep} isSelected={selectedStep === step} />
        {!isLast(i) && <Connector insert={showInsertAffordances} />}
      </div>,
    );
  }
  return <>{items}</>;
};

// Pure text summarisation for the Simplified view mode.
//
// Each step renders on its OWN line, led by its block icon. A scannable
// icon-per-line list rather than one flowing paragraph (icons landing
// mid-sentence read as visual noise). `prose` (hand-crafted paragraphs on the
// AutomationDef) keeps its paragraph grouping as spacing; otherwise the steps
// are auto-woven into sentence fragments, one per line.
const SimplifiedLine = ({ kind, text }: { kind: StepKind; text: string }) => {
  const style = STEP_STYLES[kind] ?? STEP_STYLES.action;
  const Icon = style.icon;
  return (
    <div className="flex items-start gap-3">
      <span
        className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${style.chip}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="text-[17px] leading-relaxed text-foreground">{text}</span>
    </div>
  );
};

const SimplifiedText = ({ steps, prose }: { steps: AutomationStep[]; prose?: { kind: StepKind; text: string }[][] }) => {
  if (prose?.length) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-12">
        <div className="max-w-2xl space-y-6">
          {prose.map((paragraph, pi) => (
            <div key={pi} className="space-y-3">
              {paragraph.map((entry, ei) => (
                <SimplifiedLine key={ei} kind={entry.kind} text={entry.text} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // De-capitalise a word that only starts with a capital for sentence position,
  // but keep genuine acronyms like "AI" and an already-lowercase word intact.
  const decap = (s: string) => {
    if (/^[A-Z]{2,}/.test(s)) return s;
    if (s.startsWith("The AI ")) return "the AI " + s.slice(7);
    return s.charAt(0).toLowerCase() + s.slice(1);
  };
  // Trim trailing period and lower an all-caps "NOT" so it reads as prose.
  const tidy = (s: string) => s.replace(/\.$/, "").replace(/\bNOT\b/g, "not").trim();

  // Each segment carries its block kind (for the icon) and a text fragment.
  const segments: { kind: StepKind; text: string }[] = [];
  let pendingCondition = false;

  steps.forEach((step, i) => {
    const clean = tidy(step.description || step.label);
    const isLast = i === steps.length - 1;

    if (step.kind === "trigger") {
      const text = /^when\b/i.test(clean) ? `${clean},` : `When ${decap(clean)},`;
      segments.push({ kind: step.kind, text });
    } else if (step.kind === "condition") {
      pendingCondition = true;
      if (step.exceptions?.length) {
        const conds = step.exceptions.map((ex) => decap(tidy(ex)));
        const joined =
          conds.length === 1
            ? conds[0]
            : `${conds.slice(0, -1).join(", ")}, and ${conds[conds.length - 1]}`;
        segments.push({ kind: step.kind, text: `it first checks that ${joined}.` });
      } else {
        segments.push({ kind: step.kind, text: `it ${decap(clean)}.` });
      }
    } else if (step.kind === "loop") {
      const count = step.loopBodyLength ?? 0;
      const rules = (step.stopRules ?? []).map((rule) => decap(tidy(rule)));
      const rounds = step.params?.find((param) => param.label === "Max rounds")?.values[0];
      segments.push({
        kind: step.kind,
        text: `It then repeats the next ${count} step${count === 1 ? "" : "s"}${
          rules.length ? ` until ${rules.join(" or ")}` : ""
        }${rounds ? `, at most ${rounds} times` : ""}:`,
      });
    } else if (step.kind === "decision") {
      const prefix = pendingCondition ? "If so, " : "Then, ";
      segments.push({ kind: step.kind, text: `${prefix}${decap(clean)}` });
      pendingCondition = false;
    } else if (step.kind === "execution" || step.kind === "action") {
      const prev = i > 0 ? steps[i - 1].kind : null;
      if (prev === "decision") {
        segments.push({ kind: step.kind, text: `and ${decap(clean)}.` });
      } else {
        const prefix = pendingCondition ? "If so, it " : isLast ? "Finally, it " : "It then ";
        segments.push({ kind: step.kind, text: `${prefix}${decap(clean)}.` });
        pendingCondition = false;
      }
    } else {
      // Delay / other blocks.
      segments.push({ kind: step.kind, text: `Then it ${decap(clean)}.` });
    }
  });

  return (
    <div className="flex min-h-[400px] items-center justify-center p-12">
      <div className="max-w-2xl space-y-3 select-all">
        {segments.map((seg, i) => (
          <SimplifiedLine key={i} kind={seg.kind} text={seg.text} />
        ))}
      </div>
    </div>
  );
};

const BlockSettingsPanel = ({
  step,
  onCancel,
  onSave,
}: {
  step: AutomationStep;
  onCancel: () => void;
  onSave: (step: AutomationStep) => void;
}) => {
  const [draft, setDraft] = useState<AutomationStep>({
    ...step,
    description: step.description ?? step.label,
  });

  useEffect(() => {
    setDraft({
      ...step,
      description: step.description ?? step.label,
      params: step.params?.map((param) => ({ ...param, values: [...param.values] })),
      exceptions: step.exceptions ? [...step.exceptions] : undefined,
      stopRules: step.stopRules ? [...step.stopRules] : undefined,
    });
  }, [step]);

  const updateParamValue = (paramIndex: number, valueIndex: number, value: string) => {
    setDraft((current) => {
      if (!current.params) return current;
      const params = current.params.map((param, index) => (
        index === paramIndex
          ? {
              ...param,
              values: param.values.map((existing, candidateIndex) => (
                candidateIndex === valueIndex ? value : existing
              )),
            }
          : param
      ));
      return { ...current, params };
    });
  };

  const updateStopRule = (ruleIndex: number, value: string) => {
    setDraft((current) => ({
      ...current,
      stopRules: current.stopRules?.map((rule, index) => index === ruleIndex ? value : rule),
    }));
  };

  return (
    <div>
      <div className="border-b border-border px-4 py-4">
        <h3 className="font-heading text-sm font-semibold text-foreground">Block settings</h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Dibs manages the workflow structure.
        </p>
      </div>

      <form
        className="space-y-5 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(draft);
        }}
      >
          <div className="space-y-1.5">
            <label htmlFor="block-description" className="text-sm font-medium text-foreground">
              Description
            </label>
            <Textarea
              id="block-description"
              rows={3}
              value={draft.description ?? ""}
              onChange={(event) => setDraft((current) => ({
                ...current,
                description: event.target.value,
              }))}
            />
          </div>

          {draft.params?.map((param, paramIndex) => (
            <fieldset key={`${param.label}-${paramIndex}`} className="space-y-2">
              <legend className="text-sm font-medium text-foreground">{param.label}</legend>
              {param.values.map((value, valueIndex) => (
                param.label === "Max rounds" ? (
                  <Select
                    key={valueIndex}
                    value={value}
                    onValueChange={(nextValue) => updateParamValue(paramIndex, valueIndex, nextValue)}
                  >
                    <SelectTrigger aria-label={`${param.label} value ${valueIndex + 1}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MAX_ROUND_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    key={valueIndex}
                    aria-label={`${param.label} value ${valueIndex + 1}`}
                    value={value}
                    onChange={(event) => updateParamValue(paramIndex, valueIndex, event.target.value)}
                  />
                )
              ))}
            </fieldset>
          ))}

          {draft.stopRules?.length ? (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-foreground">Stop when</legend>
              {draft.stopRules.map((rule, ruleIndex) => (
                <Input
                  key={ruleIndex}
                  aria-label={`Stop condition ${ruleIndex + 1}`}
                  value={rule}
                  onChange={(event) => updateStopRule(ruleIndex, event.target.value)}
                />
              ))}
            </fieldset>
          ) : null}

          {draft.exceptions?.length ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">If conditions</p>
              {draft.exceptions.map((condition, index) => (
                <Select key={index} value={condition}>
                  <SelectTrigger aria-label={`If condition ${index + 1}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={condition}>{condition}</SelectItem>
                  </SelectContent>
                </Select>
              ))}
            </div>
          ) : null}

          {draft.kind === "execution" && (
            <div className="flex items-center justify-between gap-4 rounded-lg border px-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-foreground">Require approval</p>
                <p className="text-xs text-muted-foreground">Wait for a person before this action runs.</p>
              </div>
              <Switch
                checked={draft.requiresApproval === true}
                onCheckedChange={(checked) => setDraft((current) => ({
                  ...current,
                  requiresApproval: checked,
                }))}
                aria-label="Require approval"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" className="standard-action-button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" size="sm" className="standard-action-button">Save changes</Button>
          </DialogFooter>
      </form>
    </div>
  );
};

const StepDiagram = ({
  steps,
  prose,
  initialViewMode = "simplified",
  showViewSwitcher = true,
  editable = false,
  showInsertAffordances = true,
  onStepsChange,
}: {
  steps: AutomationStep[];
  prose?: { kind: StepKind; text: string }[][];
  initialViewMode?: "simplified" | "detailed";
  showViewSwitcher?: boolean;
  editable?: boolean;
  showInsertAffordances?: boolean;
  onStepsChange?: (steps: AutomationStep[]) => void;
}) => {
  const [viewMode, setViewMode] = useState<"simplified" | "detailed">(initialViewMode);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);

  const openStepSettings = editable
    ? (step: AutomationStep) => setEditingStepIndex(steps.indexOf(step))
    : undefined;
  const editingStep = editingStepIndex === null ? null : steps[editingStepIndex] ?? null;

  // Segment the step sequence at every branch condition (a condition with exceptions).
  // Each segment has a `head` (the steps before the condition) and a `condition`
  // (null only for the final tail segment). This supports workflows with multiple
  // branch points. Each renders as its own BranchSplit in the canvas.
  type Segment = { head: AutomationStep[]; condition: AutomationStep | null };
  const segments: Segment[] = (() => {
    const result: Segment[] = [];
    let rest = steps;
    while (rest.length > 0) {
      // A condition inside a loop is drawn in the loop's frame, not as a split.
      const idx = rest.findIndex((s) => s.kind === "condition" && !!s.exceptions?.length && !s.loopId);
      if (idx === -1) { result.push({ head: rest, condition: null }); break; }
      result.push({ head: rest.slice(0, idx), condition: rest[idx] });
      rest = rest.slice(idx + 1);
    }
    if (result.length === 0 || result[result.length - 1].condition !== null) {
      result.push({ head: [], condition: null });
    }
    return result;
  })();

  const hasBranches = segments.some((s) => s.condition !== null);
  const initialHead = segments[0].head;
  const finalHead = segments[segments.length - 1].head;
  // All segments that have a condition (i.e. everything except the tail segment).
  const branchSegs = segments.slice(0, -1) as Array<Segment & { condition: AutomationStep }>;
  const branchConditionColumns = (() => {
    let activeColumn: "left" | "right" = "left";
    return branchSegs.map((segment) => {
      const conditionColumn = activeColumn;
      activeColumn = conditionContinueOn(segment.condition) === "yes" ? "left" : "right";
      return conditionColumn;
    });
  })();

  return (
    <div className="space-y-3">
      {/* Block palette — the primitives a workflow is assembled from. */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          Blocks
        </span>
        {PALETTE_KINDS.map((kind) => {
          const s = STEP_STYLES[kind];
          const Icon = s.icon;
          return (
            <Tooltip key={kind}>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-foreground cursor-default">
                  <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${s.chip}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  {s.label}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-[200px]">{s.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      <div className={editable ? "grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]" : undefined}>
        {/* Canvas */}
        <div
          className="relative min-h-[400px] overflow-x-auto rounded-xl border border-border bg-muted/10 p-6"
          style={{
            backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
          }}
        >
        {/* View Switcher */}
        {showViewSwitcher && (
          <div className="absolute right-4 top-4 z-10 flex h-9 items-center justify-center rounded-lg bg-card p-1 text-muted-foreground border border-border shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("simplified")}
              className={`inline-flex h-full items-center justify-center whitespace-nowrap rounded-md px-3 text-xs font-medium transition-all ${
                viewMode === "simplified"
                  ? "bg-muted text-foreground shadow-sm"
                  : "hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <List className="mr-1.5 h-3.5 w-3.5" />
              Simplified
            </button>
            <button
              type="button"
              onClick={() => setViewMode("detailed")}
              className={`inline-flex h-full items-center justify-center whitespace-nowrap rounded-md px-3 text-xs font-medium transition-all ${
                viewMode === "detailed"
                  ? "bg-muted text-foreground shadow-sm"
                  : "hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <Workflow className="mr-1.5 h-3.5 w-3.5" />
              Detailed
            </button>
          </div>
        )}

        {viewMode === "simplified" ? (
          <SimplifiedText steps={steps} prose={prose} />
        ) : (
          <div className={`mx-auto flex flex-col items-stretch ${hasBranches ? "w-[792px]" : "w-[384px]"}`}>
            {/* Steps before the first branch (or all steps if no branches). */}
            <div className={`flex flex-col items-stretch w-[384px] ${hasBranches ? "mr-auto" : "mx-auto"}`}>
              <FlowCap label="Workflow starts" />
              <Connector />
              <LinearSteps
                steps={initialHead}
                onEditStep={openStepSettings}
                selectedStep={editingStep}
                showInsertAffordances={showInsertAffordances}
              />
            </div>

            {hasBranches ? (
              branchSegs.map((seg, i) => {
                const isLastBranch = i === branchSegs.length - 1;
                // The "If" continuation: steps between this condition and the next one
                // (or the tail steps after the last condition).
                const nextHead = isLastBranch ? finalHead : segments[i + 1].head;
                return (
                  <BranchSplit
                    key={i}
                    conditionStep={seg.condition}
                    conditionColumn={branchConditionColumns[i]}
                    onEditStep={openStepSettings}
                    selectedStep={editingStep}
                    continueBranch={
                      isLastBranch ? (
                        <>
                          <LinearSteps
                            steps={nextHead}
                            onEditStep={openStepSettings}
                            selectedStep={editingStep}
                            showInsertAffordances={showInsertAffordances}
                          />
                          <Connector />
                          <FlowCap label="Workflow ends" />
                        </>
                      ) : (
                        nextHead.length > 0 ? (
                          <LinearSteps
                            steps={nextHead}
                            onEditStep={openStepSettings}
                            selectedStep={editingStep}
                            showInsertAffordances={showInsertAffordances}
                          />
                        ) : null
                      )
                    }
                  />
                );
              })
            ) : (
              <div className="flex flex-col items-stretch w-[384px] mx-auto">
                <Connector />
                <FlowCap label="Workflow ends" />
              </div>
            )}
          </div>
        )}
        </div>

        {editable && (
          <aside
            aria-label="Block settings"
            className="min-h-[400px] overflow-hidden rounded-xl border border-border bg-card lg:sticky lg:top-3"
          >
            {editingStep ? (
              <BlockSettingsPanel
                key={editingStepIndex}
                step={editingStep}
                onCancel={() => setEditingStepIndex(null)}
                onSave={(nextStep) => {
                  if (editingStepIndex === null || !onStepsChange) return;
                  onStepsChange(steps.map((step, index) => (
                    index === editingStepIndex ? nextStep : step
                  )));
                }}
              />
            ) : (
              <div className="p-5">
                <p className="text-sm font-medium text-foreground">Select a block to view its settings.</p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  Dibs keeps the workflow structure and block copy consistent. Adjustable options appear here.
                </p>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
};

// Main

const StatusBadge = ({ enabled }: { enabled: boolean }) =>
  enabled ? (
    <Badge variant="outline" className="rounded-sm border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-medium">
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </Badge>
  ) : (
    <Badge variant="outline" className="rounded-sm border-border bg-muted text-muted-foreground text-[10px] font-medium">
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
      Inactive
    </Badge>
  );

const CONNECTOR_LABELS: Record<string, string> = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  messenger: "Facebook Messenger",
  facebook_messenger: "Facebook Messenger",
  facebook: "Facebook",
  email: "Email",
};

const SUPPORTED_MESSAGING_CONNECTORS = [
  "WhatsApp",
  "Instagram",
  "Telegram",
  "Facebook Messenger",
] as const;

const BOOKING_TRIGGER_EVENTS = new Set([
  "trial_or_first_visit_completed",
  "booking_upcoming",
  "no_show",
  "booking_completed",
  "booking_created",
  "booking_cancelled",
]);

const MESSAGING_ACTIONS = new Set(["send_message", "request_feedback", "schedule_followup"]);

const connectorLabel = (connector: string) =>
  CONNECTOR_LABELS[connector]
  ?? connector.replace(/(^|[_-])(\w)/g, (_, __, letter: string) => ` ${letter.toUpperCase()}`).trim();

const ConnectorMark = ({ connector }: { connector: string }) => {
  const normalizedConnector = connector.toLowerCase();
  const icon =
    normalizedConnector === "whatsapp" ? <WhatsAppIcon size={18} /> :
    normalizedConnector === "telegram" ? <TelegramIcon size={18} /> :
    normalizedConnector === "instagram" ? <InstagramIcon size={18} /> :
    normalizedConnector === "facebook messenger" || normalizedConnector === "messenger" ? <MessengerIcon size={21} /> :
    normalizedConnector === "facebook" ? <FacebookIcon size={18} /> :
    normalizedConnector === "email" ? <GmailIcon size={21} /> :
    null;

  if (!icon) {
    return (
      <Badge
        variant="outline"
        className="rounded-sm bg-muted/40 text-[10px] font-medium text-foreground"
      >
        {connector}
      </Badge>
    );
  }

  return (
    <span
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center"
      title={connector}
      aria-label={connector}
    >
      {icon}
    </span>
  );
};

const involvedWorkflowConnectors = (definition: WorkflowDefinition): string[] => {
  const connectors = new Set<string>();
  const triggerEvent = String(definition.nodes[0]?.parameters?.event ?? "");

  if (BOOKING_TRIGGER_EVENTS.has(triggerEvent)) connectors.add("Dibs Bookings");
  if (triggerEvent === "complaint_received") {
    connectors.add("Google Reviews");
    connectors.add("Email");
  }
  if (
    triggerEvent === "lead_replied_then_went_quiet" ||
    triggerEvent === "no_reply_to_last_message"
  ) {
    SUPPORTED_MESSAGING_CONNECTORS.forEach((channel) => connectors.add(channel));
  }

  for (const node of definition.nodes) {
    if (node.type !== "execution") continue;
    const action = String(node.parameters.action ?? "");
    const onChannel = String(node.parameters.onChannel ?? "any");

    if (MESSAGING_ACTIONS.has(action)) {
      if (onChannel !== "any") {
        connectors.add(connectorLabel(onChannel));
      } else {
        SUPPORTED_MESSAGING_CONNECTORS.forEach((channel) => connectors.add(channel));
      }
      if (node.parameters.emailFallback === true) connectors.add("Email");
    }
    if (["escalate_conversation", "notify_owner", "issue_recovery_offer"].includes(action)) {
      connectors.add("Email");
    }
    if (action === "reply_to_complaint") {
      connectors.add("Google Reviews");
      connectors.add("Email");
    }
    if (["cancel_booking", "reschedule_booking", "fill_from_waitlist"].includes(action)) {
      connectors.add("Dibs Bookings");
    }
  }

  return [...connectors];
};

// Derived per-automation run stats.
interface AutomationStats {
  list: AutomationRun[];
  totalRuns: number;
  totalItems: number;
  lastRun: AutomationRun | null;
}

interface WorkflowRunSummary {
  automation_key: string;
  run_count: number | string;
  message_count: number | string;
  last_run_id: string;
  last_run_status: string;
  last_run_detail: string | null;
  last_run_item_count: number;
  last_run_at: string;
}

interface WorkflowLogItem {
  id: string;
  status: string;
  summary: string | null;
  itemCount: number;
  startedAt: string;
  endedAt: string | null;
  timingRecorded: boolean;
  error: string | null;
  currentNodeId: string | null;
  subject: Record<string, unknown> | null;
  events: CustomRunRow["events"];
}

type RunLogFilter = "all" | "attention";

const ATTENTION_RUN_STATUSES = new Set(["error", "failed"]);

function runStatusPresentation(status: string): {
  label: string;
  className: string;
} {
  switch (status) {
    case "sent":
    case "completed":
      return {
        label: "Completed",
        className: "border-emerald-200 bg-emerald-50 text-emerald-700",
      };
    case "running":
    case "waiting":
      return {
        label: "In progress",
        className: "border-blue-200 bg-blue-50 text-blue-700",
      };
    case "error":
    case "failed":
      return {
        label: "Failed",
        className: "border-red-200 bg-red-50 text-red-700",
      };
    case "skipped_disabled":
      return {
        label: "Skipped",
        className: "border-border bg-muted text-muted-foreground",
      };
    case "ended_condition":
      return {
        label: "Checkpoint ended",
        className: "border-amber-200 bg-amber-50 text-amber-700",
      };
    case "ended_abandoned":
      return {
        label: "Timing review ended",
        className: "border-amber-200 bg-amber-50 text-amber-700",
      };
    case "cancelled":
      return {
        label: "Cancelled",
        className: "border-border bg-muted text-muted-foreground",
      };
    case "no_op":
      return {
        label: "No action needed",
        className: "border-border bg-muted text-muted-foreground",
      };
    default:
      return {
        label: status.replace(/_/g, " "),
        className: "border-border bg-muted text-muted-foreground",
      };
  }
}

function exactTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

function runDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return "Still running";
  const seconds = Math.max(
    0,
    Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000),
  );
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

const RunStatusBadge = ({ status }: { status: string }) => {
  const presentation = runStatusPresentation(status);
  return (
    <Badge
      variant="outline"
      className={`${presentation.className} whitespace-nowrap text-[10px] font-medium`}
    >
      {presentation.label}
    </Badge>
  );
};

const WorkflowRunLogs = ({
  items,
  description,
  totalRuns = items.length,
}: {
  items: WorkflowLogItem[];
  description: string;
  totalRuns?: number;
}) => {
  const [filter, setFilter] = useState<RunLogFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(items[0]?.id ?? null);
  const [visibleLimit, setVisibleLimit] = useState(10);
  const firstItemId = items[0]?.id ?? null;
  useEffect(() => {
    if (firstItemId) {
      setExpandedId((current) => current ?? firstItemId);
    }
  }, [firstItemId]);
  const attentionCount = items.filter((item) => ATTENTION_RUN_STATUSES.has(item.status)).length;
  const hasExecutionTiming = items.some((item) => item.timingRecorded);
  const visibleItems = filter === "attention"
    ? items.filter((item) => ATTENTION_RUN_STATUSES.has(item.status))
    : items;
  const displayedItems = visibleItems.slice(0, visibleLimit);

  if (items.length === 0) return null;

  return (
    <section
      aria-labelledby="workflow-run-logs-heading"
      className="space-y-3 border-t border-border pt-6"
      data-testid="workflow-run-logs"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h3 id="workflow-run-logs-heading" className="text-sm font-semibold text-foreground">
              Run logs
            </h3>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {description}
            {totalRuns > items.length
              ? ` Showing the latest ${items.length} of ${totalRuns} runs.`
              : ""}
          </p>
        </div>
        <div className="flex items-center rounded-lg border border-border bg-muted/40 p-1">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              filter === "all"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            All ({items.length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("attention")}
            disabled={attentionCount === 0}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              filter === "attention"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Needs attention ({attentionCount})
          </button>
        </div>
      </div>

      {visibleItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-5 text-sm text-muted-foreground">
          No failed runs in the loaded history.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="hidden grid-cols-[minmax(0,1.5fr)_minmax(130px,0.7fr)_100px_28px] gap-4 border-b border-border bg-muted/35 px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:grid">
            <span>Run</span>
            <span>{hasExecutionTiming ? "Started" : "Recorded"}</span>
            <span>Actions</span>
            <span className="sr-only">Expand</span>
          </div>

          {displayedItems.map((item) => {
            const expanded = expandedId === item.id;
            const subjectEntries = Object.entries(item.subject ?? {}).filter(
              ([, value]) =>
                typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean",
            );

            return (
              <div key={item.id} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpandedId(expanded ? null : item.id)}
                  className="grid w-full gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/30 sm:grid-cols-[minmax(0,1.5fr)_minmax(130px,0.7fr)_100px_28px] sm:items-center sm:gap-4"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <RunStatusBadge status={item.status} />
                      <span className="truncate text-xs font-medium text-foreground">
                        {item.summary || `Run ${item.id.slice(0, 8)}`}
                      </span>
                    </span>
                    <span className="mt-1 block font-mono text-[10px] text-muted-foreground sm:hidden">
                      {item.id}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {exactTime(item.startedAt)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {item.itemCount} {item.itemCount === 1 ? "action" : "actions"}
                  </span>
                  <ChevronRight
                    className={`hidden h-4 w-4 text-muted-foreground transition-transform sm:block ${
                      expanded ? "rotate-90" : ""
                    }`}
                  />
                </button>

                {expanded && (
                  <div className="border-t border-border/70 bg-muted/20 px-4 py-4">
                    <div className="grid gap-4 text-xs sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Run ID</p>
                        <p className="mt-1 break-all font-mono text-foreground">{item.id}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Duration</p>
                        <p className="mt-1 text-foreground">
                          {item.timingRecorded
                            ? runDuration(item.startedAt, item.endedAt)
                            : "Not recorded"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          {item.timingRecorded ? "Finished" : "Recorded at"}
                        </p>
                        <p className="mt-1 text-foreground">
                          {item.timingRecorded
                            ? item.endedAt
                              ? exactTime(item.endedAt)
                              : "In progress"
                            : exactTime(item.startedAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Current step</p>
                        <p className="mt-1 break-all text-foreground">{item.currentNodeId || "—"}</p>
                      </div>
                    </div>

                    {item.error && (
                      <div className="mt-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                          <p className="font-medium">Failure detail</p>
                          <p className="mt-1 break-words font-mono text-[11px]">{item.error}</p>
                        </div>
                      </div>
                    )}

                    {subjectEntries.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Run subject
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {subjectEntries.map(([key, value]) => (
                            <span
                              key={key}
                              className="rounded-md border border-border bg-background px-2 py-1 font-mono text-[10px] text-muted-foreground"
                            >
                              {key}: {String(value)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {item.events.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Event trail
                        </p>
                        <ol className="mt-2 space-y-2 border-l border-border pl-4">
                          {item.events.map((event, index) => (
                            <li key={`${event.created_at}-${index}`} className="relative">
                              <span className="absolute -left-[19px] top-1 h-2 w-2 rounded-full border border-border bg-background" />
                              <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <p className="text-xs text-foreground">{event.summary}</p>
                                <time className="text-[10px] text-muted-foreground">
                                  {exactTime(event.created_at)}
                                </time>
                              </div>
                              <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                                {event.kind}
                                {event.node_id ? ` · ${event.node_id}` : ""}
                              </p>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {displayedItems.length < visibleItems.length && (
            <div className="border-t border-border bg-muted/20 p-3 text-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setVisibleLimit((current) => current + 10)}
              >
                Show 10 more
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const Automations = ({
  onNavigateToSection,
  onBuilderNameChange,
  onDetailChange,
}: AutomationsProps = {}) => {
  const { businessId, isSupportCollaborator } = useAuth();
  const { isAllOutlets } = useOutlet();
  const { subscriptionTier, billingCycleType, refresh: refreshSubscription } = useSubscription();
  const { hasConnectedChannel } = useChannelConnection(businessId);
  const queryClient = useQueryClient();
  // Plan gate. The $1 Builder plan can import contacts and build profiles but
  // cannot run automations. Mirrors planAllowsAutomations() on the backend (the
  // real enforcement); this just keeps the UI honest. Unknown / not-yet-loaded
  // tier defaults to allowed so paying businesses are never wrongly locked out.
  const automationsAllowed = !subscriptionTier || (plans[subscriptionTier]?.features.automations ?? true);
  // Starter and Sandbox use templates as-is and don't have Ask Dibs AI, so
  // "Start from scratch" and the Copilot aren't offered (the server agrees).
  const canBuildWorkflows = planHasFeature(subscriptionTier, "custom_workflows");
  const canAskDibs = planHasFeature(subscriptionTier, "ask_dibs_ai");

  // The plan the owner intends to pay for once they go live (chosen at onboarding,
  // stored in businesses.temporary_metadata.selectedPlan). Turning on a workflow
  // from Sandbox charges this plan. Fall back to Growth if it's missing or is a
  // non-self-serve pick (builder / enterprise / free).
  const { data: intendedPlanRaw } = useQuery({
    queryKey: ["intended-plan", businessId],
    queryFn: async (): Promise<string | null> => {
      if (!businessId) return null;
      const { data } = await supabase
        .from("businesses")
        .select("temporary_metadata")
        .eq("business_id", businessId)
        .maybeSingle();
      const md = (data?.temporary_metadata ?? null) as { selectedPlan?: string } | null;
      return md?.selectedPlan ?? null;
    },
    enabled: !!businessId,
  });
  const intendedPlanKey = ["starter", "growing", "premium"].includes(intendedPlanRaw ?? "")
    ? (intendedPlanRaw as string)
    : "growing";
  const upgradeCycle: BillingCycle = billingCycleType ?? "monthly";
  const intendedPlanCfg = plans[intendedPlanKey];
  const upgradePriceId = planPriceIdForCycle(intendedPlanCfg, upgradeCycle);
  // The confirm dialog shows what Stripe actually charges for the cycle
  // (plan prices are per-month equivalents).
  const upgradeCharge = planChargeForCycle(intendedPlanCfg, upgradeCycle);
  // Per-month price for the "/mo" banner.
  const upgradePrice = intendedPlanCfg?.price[upgradeCycle];

  // Upgrade-on-enable flow: a Sandbox business turning a workflow on must go live
  // (channel connected) and start paying its plan. We confirm, swap the $1 price
  // for the plan price in place (update-subscription, prorated), then enable.
  // pendingKey may also be a custom workflow's `custom_…` settings key.
  const [upgrade, setUpgrade] = useState<{ open: boolean; pendingKey: string | null; busy: boolean }>({
    open: false,
    pendingKey: null,
    busy: false,
  });
  // Selection lives in the URL (?workflow=) so the AdminLayout breadcrumb can
  // reflect the open workflow and the "Workflows" crumb can navigate back.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("workflow");
  const setSelectedId = (id: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("workflow", id);
        else next.delete("workflow");
        return next;
      },
      { replace: true },
    );
  };
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const selected = AUTOMATIONS.find((a) => a.id === selectedId) || null;
  // The workflow builder opens via ?builder=new|<workflow_definitions.id>.
  const builderId = searchParams.get("builder");
  const setBuilderId = (id: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("builder", id);
        else next.delete("builder");
        next.delete("workflow");
        return next;
      },
      { replace: true },
    );
  };
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);
  // Workflow Copilot. Chat-first workflow creation. UI only for now: replies
  // come from the mock transport until the workflow-copilot edge fn ships.
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [catalogTab, setCatalogTab] = useState<CatalogTab>("capabilities");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [businessTypeFilters, setBusinessTypeFilters] = useState<CatalogBusinessType[]>([]);
  const [objectiveFilters, setObjectiveFilters] = useState<CatalogObjective[]>([]);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<"template" | "capability">("template");
  const [capabilityStepOverrides, setCapabilityStepOverrides] = useState<Record<string, AutomationStep[]>>({});

  // Owner-built workflows (RLS-scoped read; writes go through the edge fn).
  const {
    data: customWorkflows = [],
    isPending: customWorkflowsPending,
    isError: customWorkflowsFailed,
    refetch: refetchCustomWorkflows,
  } = useQuery({
    queryKey: ["workflow-definitions", businessId],
    queryFn: async (): Promise<CustomWorkflowRow[]> => {
      if (!businessId) return [];
      const { data, error } = await (supabase as any)
        .from("workflow_definitions")
        .select("id, key, name, description, status, definition, version, source_template_id, updated_at")
        .eq("business_id", businessId)
        .neq("status", "archived")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return ((data ?? []) as CustomWorkflowRow[]).filter(
        (workflow) => !workflow.source_template_id?.startsWith("system:"),
      );
    },
    enabled: !!businessId,
  });
  const selectedCustom = customWorkflows.find((w) => w.id === selectedId) || null;

  useEffect(() => {
    if (builderId || !selectedId) {
      onDetailChange?.(null);
      return;
    }
    const name = selectedCustom?.name ?? selected?.name;
    onDetailChange?.(name ? { id: selectedId, name } : null);
  }, [builderId, onDetailChange, selected, selectedCustom, selectedId]);

  // Interpreter run history for the open custom workflow, with the per-run
  // event trail (incl. the AI's time/content adjustment explanations).
  const { data: customRuns = [] } = useQuery({
    queryKey: ["workflow-runs", businessId, selectedCustom?.id],
    queryFn: async (): Promise<CustomRunRow[]> => {
      if (!businessId || !selectedCustom) return [];
      const { data: runs } = await (supabase as any)
        .from("workflow_runs")
        .select("id, status, started_at, ended_at, error, current_node_id, subject")
        .eq("business_id", businessId)
        .eq("workflow_id", selectedCustom.id)
        .order("started_at", { ascending: false })
        .limit(20);
      const runList = (runs ?? []) as Array<{
        id: string;
        status: string;
        started_at: string;
        ended_at: string | null;
        error: string | null;
        current_node_id: string | null;
        subject: Record<string, unknown> | null;
      }>;
      if (runList.length === 0) return [];
      const { data: events } = await (supabase as any)
        .from("workflow_run_events")
        .select("run_id, kind, detail, created_at, node_id")
        .eq("business_id", businessId)
        .in("run_id", runList.map((r) => r.id))
        .order("created_at", { ascending: true })
        .limit(200);
      const byRun = new Map<string, CustomRunRow["events"]>();
      for (const e of (events ?? []) as Array<{
        run_id: string;
        kind: string;
        detail: { summary?: string } | null;
        created_at: string;
        node_id: string | null;
      }>) {
        const list = byRun.get(e.run_id) ?? [];
        list.push({
          kind: e.kind,
          summary: e.detail?.summary ?? e.kind,
          created_at: e.created_at,
          node_id: e.node_id,
        });
        byRun.set(e.run_id, list);
      }
      return runList.map((r) => ({
        ...r,
        subject: r.subject ?? {},
        events: byRun.get(r.id) ?? [],
      }));
    },
    enabled: SHOW_WORKFLOW_RUN_LOGS && !!businessId && !!selectedCustom,
    refetchInterval: 60_000,
  });

  // Per-(business, automation) enable state. Only rows that are OFF are stored;
  // a missing key means enabled (opt-out model, mirrors the backend helper).
  // automation_settings/automation_runs aren't in the generated types until the
  // migration is deployed + types regenerated. Cast per codebase convention.
  const {
    data: settings = {},
    isPending: settingsPending,
    isError: settingsFailed,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ["automation-settings", businessId],
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!businessId) return {};
      const { data, error } = await (supabase as any)
        .from("automation_settings")
        .select("automation_key, enabled")
        .eq("business_id", businessId);
      if (error) throw error;
      const map: Record<string, boolean> = {};
      for (const r of (data ?? []) as Array<{ automation_key: string; enabled: boolean }>) {
        map[r.automation_key] = r.enabled;
      }
      return map;
    },
    enabled: !!businessId,
  });

  const {
    data: runSummaries = [],
    isPending: runSummariesPending,
    isError: runSummariesFailed,
    refetch: refetchRunSummaries,
  } = useQuery({
    queryKey: ["workflow-run-summaries", businessId],
    queryFn: async (): Promise<WorkflowRunSummary[]> => {
      if (!businessId) return [];
      const { data, error } = await (supabase as any)
        .from("workflow_run_summaries")
        .select(
          "automation_key, run_count, message_count, last_run_id, last_run_status, last_run_detail, last_run_item_count, last_run_at",
        )
        .eq("business_id", businessId);
      if (error) throw error;
      return (data ?? []) as WorkflowRunSummary[];
    },
    enabled: !!businessId,
    refetchInterval: 60_000,
  });

  // Detailed built-in logs are fetched only for the workflow the owner opens.
  // Custom workflows use workflow_runs + workflow_run_events above.
  const { data: selectedDefaultRuns = [] } = useQuery({
    queryKey: ["automation-run-logs", businessId, selected?.key],
    queryFn: async (): Promise<AutomationRun[]> => {
      if (!businessId || !selected) return [];
      const { data, error } = await (supabase as any)
        .from("automation_runs")
        .select("id, automation_key, status, detail, item_count, created_at")
        .eq("business_id", businessId)
        .eq("automation_key", selected.key)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as AutomationRun[];
    },
    enabled: SHOW_WORKFLOW_RUN_LOGS && !!businessId && !!selected,
    refetchInterval: 60_000,
  });

  const isEnabled = (key: string) => {
    if (key in settings) return settings[key];
    // No row yet: custom workflows and opt-in automations default OFF (the
    // backend's isAutomationOptedIn fails closed), the rest default ON.
    // "Custom" is determined by the fetched workflow rows. Never by key
    // naming conventions, which live in the edge function and could change.
    if (customWorkflows.some((w) => w.key === key)) return false;
    return !AUTOMATIONS.find((a) => a.key === key)?.defaultOff;
  };

  const statsFor = (key: string): AutomationStats => {
    const summary = runSummaries.find((item) => item.automation_key === key);
    const list = selected?.key === key ? selectedDefaultRuns : [];
    const lastRun = summary
      ? {
          id: summary.last_run_id,
          automation_key: summary.automation_key,
          status: summary.last_run_status,
          detail: summary.last_run_detail,
          item_count: summary.last_run_item_count,
          created_at: summary.last_run_at,
        }
      : list[0] ?? null;
    return {
      list,
      totalRuns: Number(summary?.run_count ?? list.length),
      totalItems: Number(summary?.message_count ?? 0),
      lastRun,
    };
  };

  // Persist a single workflow enable/disable (no plan gating, caller decides).
  const writeToggle = async (key: string, next: boolean) => {
    if (!businessId) return false;
    setSavingKey(key);
    // Optimistic. Flip the local map immediately.
    queryClient.setQueryData(
      ["automation-settings", businessId],
      (prev: Record<string, boolean> | undefined) => ({ ...(prev ?? {}), [key]: next }),
    );
    const { error } = await (supabase as any)
      .from("automation_settings")
      .upsert(
        { business_id: businessId, automation_key: key, enabled: next, updated_at: new Date().toISOString() },
        { onConflict: "business_id,automation_key" },
      );
    setSavingKey(null);
    if (error) {
      toast.error("Couldn't update that workflow. Please try again.");
      queryClient.invalidateQueries({ queryKey: ["automation-settings", businessId] });
      return false;
    }
    return true;
  };

  const toggle = async (key: string, next: boolean) => {
    if (!businessId) return;
    if (isAllOutlets) {
      toast.info("Select an outlet to change workflow settings.");
      return;
    }
    // Sandbox business turning a workflow ON → it must go live and start paying.
    if (!automationsAllowed && next) {
      if (!hasConnectedChannel) {
        toast.error("Connect a messaging channel first to go live and turn on workflows.");
        return;
      }
      if (!upgradePriceId) {
        toast.error("Couldn't determine your plan. Please pick a plan from Billing first.");
        return;
      }
      // Confirm the charge, then upgrade in place (see confirmUpgrade).
      setUpgrade({ open: true, pendingKey: key, busy: false });
      return;
    }
    // Turning OFF from Sandbox is a no-op (nothing was running); ignore.
    if (!automationsAllowed) return;
    const ok = await writeToggle(key, next);
    if (ok) toast.success(next ? "Workflow turned on" : "Workflow turned off");
  };

  // Confirmed: swap the $1 Sandbox price for the intended plan in place (prorated),
  // then enable the pending workflow once the business is live.
  const confirmUpgrade = async () => {
    if (!upgradePriceId || !upgrade.pendingKey) return;
    const pendingKey = upgrade.pendingKey;
    setUpgrade((u) => ({ ...u, busy: true }));
    try {
      const { data, error } = await supabase.functions.invoke("update-subscription", {
        body: { targetPriceId: upgradePriceId, test_mode: isStripeTestMode },
      });
      if (error) throw new Error(error.message);
      if (data && data.success === false) throw new Error(data.error || "Upgrade failed");

      await refreshSubscription();
      const ok = await writeToggle(pendingKey, true);
      setUpgrade({ open: false, pendingKey: null, busy: false });
      if (ok) {
        toast.success(`You're live on the ${intendedPlanCfg?.name ?? "selected"} plan — workflow turned on.`);
      }
    } catch (e) {
      setUpgrade((u) => ({ ...u, busy: false }));
      toast.error(e instanceof Error ? e.message : "Couldn't complete the upgrade. Please try again.");
    }
  };

  // Edge-fn actions for owner-built workflows (writes never go via RLS).
  const invokeWorkflowFn = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("workflow-definitions", {
      body: { business_id: businessId, ...body },
    });
    if (error) throw new Error(await readEdgeError(error));
    if (data && data.success === false) throw new Error(data.error || "request failed");
    return data;
  };

  // "Duplicate & customize" a code-defined default into an editable draft.
  const duplicateTemplate = async (templateId: string): Promise<boolean> => {
    if (isAllOutlets) {
      toast.info("Select an outlet to create a workflow.");
      return false;
    }
    setDuplicatingTemplateId(templateId);
    try {
      const data = await invokeWorkflowFn({ action: "duplicate_template", template_id: templateId });
      queryClient.invalidateQueries({ queryKey: ["workflow-definitions", businessId] });
      toast.success("Copy created — customize away.");
      setBuilderId(data.workflow.id);
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't duplicate the workflow");
      return false;
    } finally {
      setDuplicatingTemplateId(null);
    }
  };

  const archiveCustom = async (id: string) => {
    if (isAllOutlets) {
      toast.info("Select an outlet to archive a workflow.");
      return;
    }
    try {
      await invokeWorkflowFn({ action: "archive", id });
      queryClient.invalidateQueries({ queryKey: ["workflow-definitions", businessId] });
      queryClient.invalidateQueries({ queryKey: ["automation-settings", businessId] });
      toast.success("Workflow archived");
      setSelectedId(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't archive the workflow");
    }
  };

  // Short human summary of the last activity for a card.
  const activitySummary = (a: AutomationDef): string => {
    if (a.noRunLog) return "Running live";
    const { lastRun, totalRuns } = statsFor(a.key);
    if (lastRun) {
      return `${totalRuns} run${totalRuns === 1 ? "" : "s"} · last ${relativeTime(lastRun.created_at)}`;
    }
    return isEnabled(a.key) ? "Waiting for its first run" : "Available to turn on";
  };

  const catalogEntries: CatalogEntry[] = [
    ...AUTOMATIONS.map((automation): CatalogEntry => ({
      id: automation.id,
      name: automation.name,
      description: DEFAULT_WORKFLOW_CARD_COPY[automation.id] ?? automation.description,
      sectionId: DEFAULT_WORKFLOW_CATALOG[automation.id],
      source: "ready",
      businessTypes: [],
      automation,
      definition: automation.definition,
      previewSteps: automation.steps,
    })),
    ...WORKFLOW_RECIPES.map((recipe): CatalogEntry => ({
      id: recipe.id,
      name: recipe.name,
      description: TEMPLATE_CARD_COPY[recipe.id] ?? recipe.description,
      sectionId: recipe.outcome === "convert" ? "growth" : recipe.outcome,
      source: "template",
      businessTypes: recipe.vertical === "elective_clinic"
        ? ["health"]
        : recipe.vertical === "ktv"
        ? ["hospitality"]
        : [],
      recipeId: recipe.id,
      definition: recipe,
      previewSteps: toDisplaySteps(recipe),
    })),
    ...customWorkflows.map((workflow): CatalogEntry => ({
      id: workflow.id,
      name: workflow.name,
      description: workflow.description || "A workflow created by your team.",
      sectionId: "custom",
      source: "custom",
      businessTypes: [],
      custom: workflow,
    })),
  ];
  const tabCatalogEntries = catalogEntries.filter((entry) => {
    if (catalogTab === "my") return entry.source === "custom";
    if (catalogTab === "templates") {
      return entry.source !== "custom" && isWorkflowTemplateVisible(entry.id);
    }
    return false;
  });
  const normalizedCatalogSearch = catalogSearch.trim().toLowerCase();
  const visibleCatalogEntries = tabCatalogEntries.filter((entry) => {
    const section = CATALOG_SECTIONS.find((candidate) => candidate.id === entry.sectionId);
    const matchesSearch = !normalizedCatalogSearch
      || `${entry.name} ${entry.description} ${section?.title ?? ""}`.toLowerCase().includes(normalizedCatalogSearch);
    const matchesBusiness = businessTypeFilters.length === 0
      || entry.businessTypes.length === 0
      || entry.businessTypes.some((businessType) => businessTypeFilters.includes(businessType));
    const matchesObjective = catalogTab !== "templates"
      || objectiveFilters.length === 0
      || objectiveFilters.includes(entry.sectionId as CatalogObjective);
    return matchesSearch && matchesBusiness && matchesObjective;
  });
  const activeCatalogFilterCount = businessTypeFilters.length + objectiveFilters.length;
  const previewTemplate = catalogEntries.find((entry) => entry.id === previewTemplateId) ?? null;
  const previewSteps = previewTemplate
    ? capabilityStepOverrides[previewTemplate.id] ?? previewTemplate.previewSteps
    : undefined;
  const previewBusinessLabel = previewTemplate?.businessTypes[0] === "health"
    ? "Health & wellness"
    : previewTemplate?.businessTypes[0] === "hospitality"
      ? "Hospitality & entertainment"
      : "Any business";
  const clearCatalogFilters = () => {
    setCatalogSearch("");
    setBusinessTypeFilters([]);
    setObjectiveFilters([]);
  };
  const toggleCatalogFilter = <T extends string>(value: T, values: T[], setValues: (next: T[]) => void) => {
    setValues(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  };

  const renderCatalogCard = (entry: CatalogEntry) => {
    const automation = entry.automation;
    const customWorkflow = entry.custom;
    const published = customWorkflow?.status === "published";
    const enabled = automation
      ? isEnabled(automation.key)
      : customWorkflow
        ? Boolean(published && isEnabled(customWorkflow.key))
        : false;
    const activity = customWorkflow
      ? (published ? (enabled ? "Running" : "Turned off") : "Draft")
      : null;
    const involvedConnectors = entry.definition
      ? involvedWorkflowConnectors(entry.definition)
      : [];

    if (entry.source === "custom") {
      return (
        <article
          key={entry.id}
          data-testid="workflow-row"
          role="button"
          tabIndex={0}
          onClick={() => setSelectedId(entry.id)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setSelectedId(entry.id);
            }
          }}
          className="group flex min-h-[88px] cursor-pointer items-center gap-4 border-b border-border bg-card px-4 py-3 text-left transition-colors first:rounded-t-lg last:rounded-b-lg last:border-b-0 hover:bg-secondary/50 focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h4 className="truncate text-sm font-heading font-semibold leading-5 text-foreground sm:text-base">
                {entry.name}
              </h4>
              {customWorkflow && !published && (
                <Badge
                  variant="outline"
                  className="shrink-0 rounded-sm bg-background px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
                >
                  Draft
                </Badge>
              )}
            </div>
            <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
              {entry.description}
            </p>
            {published && <p className="mt-1 text-[11px] text-muted-foreground">{activity}</p>}
          </div>

          {automation && (
            <div
              className="flex shrink-0 items-center gap-2"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <span className="hidden text-[11px] text-muted-foreground sm:inline">
                {enabled ? "On" : "Off"}
              </span>
              <Switch
                checked={automationsAllowed && enabled}
                disabled={isAllOutlets || savingKey === automation.key || upgrade.busy}
                onCheckedChange={(value) => toggle(automation.key, value)}
                aria-label={`Toggle ${automation.name}`}
              />
            </div>
          )}

          {customWorkflow && published && (
            <div
              className="flex shrink-0 items-center gap-2"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <span className="hidden text-[11px] text-muted-foreground sm:inline">
                {enabled ? "On" : "Off"}
              </span>
              <Switch
                checked={automationsAllowed && enabled}
                disabled={isAllOutlets || savingKey === customWorkflow.key || upgrade.busy}
                onCheckedChange={(value) => toggle(customWorkflow.key, value)}
                aria-label={`Toggle ${customWorkflow.name}`}
              />
            </div>
          )}
        </article>
      );
    }

    return (
      <button
        type="button"
        key={entry.id}
        data-testid="template-card"
        onClick={() => {
          setPreviewMode("template");
          setPreviewTemplateId(entry.id);
        }}
        className="group flex min-h-[132px] w-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="flex-1">
          <h4 className="text-base font-heading font-semibold leading-6 text-foreground">
            {entry.description}
          </h4>
        </div>
        {involvedConnectors.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-2" aria-label="Involved connectors">
            {involvedConnectors.map((connector) => (
              <ConnectorMark key={connector} connector={connector} />
            ))}
          </div>
        )}
      </button>
    );
  };

  const renderCreateWorkflowButton = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="create-workflow-button"
          className="admin-toolbar-button"
          disabled={isAllOutlets}
          title={isAllOutlets ? "Select an outlet to create a workflow" : undefined}
        >
          <Plus className="h-3.5 w-3.5" />
          Create workflow
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {/* The assistant resolves the business by ownership, so a support
            collaborator would meet a 404 here. Hide the door rather than open
            one that doesn't work (decision D1). */}
        {!isSupportCollaborator && canAskDibs && (
          <DropdownMenuItem
            data-testid="create-workflow-copilot"
            className="gap-2 focus:bg-muted/60 focus:text-foreground"
            onSelect={() => setCopilotOpen(true)}
          >
            <Bot className="h-4 w-4 text-muted-foreground" />
            Build with Copilot
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="gap-2 focus:bg-muted/60 focus:text-foreground"
          onSelect={() => {
            setCatalogTab("templates");
            setCatalogSearch("");
          }}
        >
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          From a template
        </DropdownMenuItem>
        {canBuildWorkflows ? (
          <DropdownMenuItem
            className="gap-2 focus:bg-muted/60 focus:text-foreground"
            onSelect={() => setBuilderId("new")}
          >
            <Plus className="h-4 w-4 text-muted-foreground" />
            Start from scratch
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="gap-2 focus:bg-muted/60 focus:text-foreground"
            onSelect={() => onNavigateToSection?.("settings", { settings: "billing" })}
          >
            <Lock className="h-4 w-4 text-muted-foreground" />
            <span className="flex flex-col">
              Start from scratch
              <span className="text-xs text-muted-foreground">Included from Growth</span>
            </span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Builder
  if (builderId && businessId) {
    if (isAllOutlets) {
      return (
        <div className="rounded-xl border border-border bg-card px-6 py-10 text-center">
          <p className="text-sm font-semibold text-foreground">Workflow editing is unavailable in All outlets.</p>
          <p className="mt-1 text-sm text-muted-foreground">Select an outlet, then open the workflow again.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => setBuilderId(null)}>
            Back to workflows
          </Button>
        </div>
      );
    }
    return (
      <WorkflowBuilder
        businessId={businessId}
        workflowId={builderId}
        onClose={() => setBuilderId(null)}
        onSavedNew={(id) => setBuilderId(id)}
        onNameChange={onBuilderNameChange}
        onNavigateToSection={onNavigateToSection}
      />
    );
  }

  // Custom workflow detail view
  if (selectedCustom) {
    const enabled = selectedCustom.status === "published" && isEnabled(selectedCustom.key);
    const stats = statsFor(selectedCustom.key);
    const requiresApproval = workflowRequiresApproval(selectedCustom.definition);
    const logItems: WorkflowLogItem[] = customRuns.map((run) => ({
      id: run.id,
      status: run.status,
      summary:
        [...run.events].reverse().find((event) => event.kind === "run_ended")?.summary ??
        `${selectedCustom.name} run`,
      itemCount: run.events.filter((event) => event.kind === "action_dispatched").length,
      startedAt: run.started_at,
      endedAt: run.ended_at,
      timingRecorded: true,
      error: run.error,
      currentNodeId: run.current_node_id,
      subject: run.subject,
      events: run.events,
    }));
    const involvedConnectors = involvedWorkflowConnectors(selectedCustom.definition);
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-heading font-semibold text-foreground">{selectedCustom.name}</h2>
              {selectedCustom.status === "draft" ? (
                <Badge variant="outline" className="rounded-sm border-border bg-muted text-muted-foreground text-[10px] font-medium">
                  Draft
                </Badge>
              ) : (
                <StatusBadge enabled={enabled} />
              )}
              {requiresApproval && (
                <Badge variant="outline" className="rounded-sm border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-medium">
                  Requires approval
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{selectedCustom.description || "No description yet."}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  disabled={isAllOutlets}
                  onClick={() => setBuilderId(selectedCustom.id)}
                  aria-label={`Edit ${selectedCustom.name}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit workflow</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-destructive"
                  disabled={isAllOutlets}
                  onClick={() => archiveCustom(selectedCustom.id)}
                  aria-label={`Archive ${selectedCustom.name}`}
                >
                  <Archive className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive workflow</TooltipContent>
            </Tooltip>
            {selectedCustom.status === "published" && (
              <div className="ml-1 flex items-center gap-2 border-l border-border pl-3">
                <span
                  className={`min-w-5 text-xs font-medium ${
                    automationsAllowed && enabled ? "text-emerald-700" : "text-muted-foreground"
                  }`}
                  aria-live="polite"
                >
                  {automationsAllowed && enabled ? "On" : "Off"}
                </span>
                <Switch
                  checked={automationsAllowed && enabled}
                  disabled={isAllOutlets || savingKey === selectedCustom.key || upgrade.busy}
                  onCheckedChange={(v) => toggle(selectedCustom.key, v)}
                  aria-label={`${selectedCustom.name} is ${automationsAllowed && enabled ? "on" : "off"}`}
                />
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">How it works</p>
            <StepDiagram steps={toDisplaySteps(selectedCustom.definition)} />
          </div>

          <div className="space-y-4">
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Workflow details</p>
              <dl className="divide-y divide-border rounded-xl border border-border bg-card">
                <div className="space-y-2 p-3">
                  <dt className="text-sm text-muted-foreground">Involved connectors</dt>
                  <dd className="flex flex-wrap items-center gap-2">
                    {involvedConnectors.length > 0 ? involvedConnectors.map((connector) => (
                      <ConnectorMark key={connector} connector={connector} />
                    )) : (
                      <span className="text-sm text-muted-foreground">No external connectors</span>
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3 p-3">
                  <dt className="text-sm text-muted-foreground">Version</dt>
                  <dd className="text-sm font-medium text-foreground">v{selectedCustom.version}</dd>
                </div>
              </dl>
            </div>

            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Metrics</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-card p-3 text-center">
                  <p className="text-lg font-semibold text-foreground">{stats.totalRuns}</p>
                  <p className="text-[11px] text-muted-foreground">Runs (all time)</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-3 text-center">
                  <p className="text-lg font-semibold text-foreground">{stats.totalItems}</p>
                  <p className="text-sm text-muted-foreground">Messages sent</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {SHOW_WORKFLOW_RUN_LOGS && (
          <WorkflowRunLogs
            key={selectedCustom.id}
            items={logItems}
            totalRuns={stats.totalRuns}
            description="A business-scoped audit trail of each execution, including its outcome, subject, failure detail, and step events."
          />
        )}
      </div>
    );
  }

  // Detail view
  if (selected) {
    const enabled = isEnabled(selected.key);
    const stats = statsFor(selected.key);
    const logItems: WorkflowLogItem[] = stats.list.map((run) => ({
      id: run.id,
      status: run.status,
      summary: run.detail,
      itemCount: run.item_count,
      startedAt: run.created_at,
      endedAt: null,
      timingRecorded: false,
      error: ATTENTION_RUN_STATUSES.has(run.status) ? run.detail : null,
      currentNodeId: null,
      subject: null,
      events: [],
    }));
    return (
      <div>
        <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-heading font-semibold text-foreground">{selected.name}</h2>
              <StatusBadge enabled={enabled} />
              {selected.requiresApproval && (
                <Badge variant="outline" className="rounded-sm border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-medium">
                  Requires approval
                </Badge>
              )}
              <Badge variant="outline" className="rounded-sm border-border bg-muted text-muted-foreground text-[10px] font-medium">
                Default · by Dibs
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{selected.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {selected.key === "complaint_recovery" && onNavigateToSection && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onNavigateToSection("crisis", { view: "settings" })}>
                <Link2 className="h-4 w-4" />
                Complaint sources
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={isAllOutlets || duplicatingTemplateId !== null}
              title={isAllOutlets ? "Select an outlet to customize this workflow" : undefined}
              onClick={() => duplicateTemplate(selected.id)}
            >
              {duplicatingTemplateId === selected.id ? "Copying…" : "Duplicate & customize"}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            <div className="mb-3 flex items-center gap-1.5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">How it works</p>
            </div>
            <StepDiagram steps={selected.steps} prose={selected.prose} />
          </div>

          <div className="space-y-4">
            {/* Counters */}
            {!selected.noRunLog && (
              <div>
                <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Metrics</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border bg-card p-3 text-center">
                    <p className="text-lg font-semibold text-foreground">{stats.totalRuns}</p>
                    <p className="text-[11px] text-muted-foreground">Runs (all time)</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-3 text-center">
                    <p className="text-lg font-semibold text-foreground">{stats.totalItems}</p>
                    <p className="text-sm text-muted-foreground">Messages sent</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {SHOW_WORKFLOW_RUN_LOGS && (
          <WorkflowRunLogs
            key={selected.id}
            items={logItems}
            totalRuns={stats.totalRuns}
            description="A business-scoped record of recent executions, their outcome, and how many actions each run completed."
          />
        )}
        </div>
      </div>
    );
  }

  const defaultBucket = (automation: AutomationDef) =>
    getWorkflowLifecycleBucket({
      enabled: isEnabled(automation.key),
      runCount: statsFor(automation.key).totalRuns,
    });
  const customBucket = (workflow: CustomWorkflowRow) =>
    getWorkflowLifecycleBucket({
      enabled: workflow.status === "published" && isEnabled(workflow.key),
      runCount: statsFor(workflow.key).totalRuns,
    });

  const usedDefaults = AUTOMATIONS.filter((automation) => defaultBucket(automation) === "used");
  const usedCustoms = customWorkflows.filter((workflow) => customBucket(workflow) === "used");
  const enabledDefaults = AUTOMATIONS.filter((automation) => defaultBucket(automation) === "enabled");
  const enabledCustoms = customWorkflows.filter((workflow) => customBucket(workflow) === "enabled");
  const availableDefaults = AUTOMATIONS.filter((automation) => defaultBucket(automation) === "available");
  const createdWithoutRuns = customWorkflows.filter(
    (workflow) => customBucket(workflow) === "available",
  );
  const workflowCatalogPending =
    !!businessId &&
    (customWorkflowsPending || settingsPending || runSummariesPending);
  const workflowCatalogFailed =
    customWorkflowsFailed || settingsFailed || runSummariesFailed;
  const retryWorkflowCatalog = () => {
    void Promise.all([
      refetchCustomWorkflows(),
      refetchSettings(),
      refetchRunSummaries(),
    ]);
  };

  const renderDefaultCard = (
    automation: AutomationDef,
    bucket: "used" | "enabled" | "available",
  ) => {
    const enabled = isEnabled(automation.key);
    const stats = statsFor(automation.key);
    return (
      <div
        key={automation.id}
        role="button"
        tabIndex={0}
        data-testid="workflow-card"
        data-workflow-state={bucket}
        onClick={() => setSelectedId(automation.id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setSelectedId(automation.id);
          }
        }}
        className="group flex min-h-[156px] cursor-pointer flex-col rounded-lg border border-border bg-card p-5 text-left shadow-sm transition-all duration-200 hover:bg-secondary/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h5 className="text-base font-heading font-semibold leading-5 text-foreground">
              {automation.name}
            </h5>
            {SHOW_WORKFLOW_RUN_LOGS && bucket === "used" && (
              <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-primary">
                <History className="h-3 w-3" />
                Run history available
              </span>
            )}
          </div>
          <div
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="flex shrink-0 items-center gap-2"
          >
            <span className="text-[11px] text-muted-foreground">{enabled ? "On" : "Off"}</span>
            <Switch
              checked={automationsAllowed && enabled}
              disabled={savingKey === automation.key || upgrade.busy}
              onCheckedChange={(value) => toggle(automation.key, value)}
              aria-label={`Toggle ${automation.name}`}
            />
          </div>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {DEFAULT_WORKFLOW_CARD_COPY[automation.id] ?? automation.description}
        </p>
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
          <span className="text-[11px] text-muted-foreground">{activitySummary(automation)}</span>
          {SHOW_WORKFLOW_RUN_LOGS && stats.totalRuns > 0 && (
            <span className="shrink-0 text-[11px] font-medium text-primary">View logs</span>
          )}
        </div>
      </div>
    );
  };

  const renderCustomCard = (
    workflow: CustomWorkflowRow,
    bucket: "used" | "enabled" | "available",
  ) => {
    const published = workflow.status === "published";
    const enabled = published && isEnabled(workflow.key);
    const stats = statsFor(workflow.key);
    const lastRun = stats.lastRun;
    const activity = lastRun
      ? `${stats.totalRuns} run${stats.totalRuns === 1 ? "" : "s"} · last ${relativeTime(lastRun.created_at)}`
      : published
        ? enabled
          ? "Waiting for its first run"
          : "Ready to enable"
        : "Not published yet";

    return (
      <div
        key={workflow.id}
        role="button"
        tabIndex={0}
        data-testid="workflow-card"
        data-workflow-state={bucket}
        onClick={() => setSelectedId(workflow.id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setSelectedId(workflow.id);
          }
        }}
        className="group flex min-h-[156px] cursor-pointer flex-col rounded-lg border border-border bg-card p-5 text-left shadow-sm transition-all duration-200 hover:bg-secondary/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h5 className="text-base font-heading font-semibold leading-5 text-foreground">
              {workflow.name}
            </h5>
            {SHOW_WORKFLOW_RUN_LOGS && bucket === "used" && (
              <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-primary">
                <History className="h-3 w-3" />
                Detailed run history
              </span>
            )}
          </div>
          <div
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="flex shrink-0 items-center gap-2"
          >
            {published ? (
              <>
                <span className="text-[11px] text-muted-foreground">{enabled ? "On" : "Off"}</span>
                <Switch
                  checked={automationsAllowed && enabled}
                  disabled={savingKey === workflow.key || upgrade.busy}
                  onCheckedChange={(value) => toggle(workflow.key, value)}
                  aria-label={`Toggle ${workflow.name}`}
                />
              </>
            ) : (
              <Badge variant="outline" className="border-border bg-muted text-[10px] text-muted-foreground">
                Draft
              </Badge>
            )}
          </div>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {workflow.description || "A workflow created by your team."}
        </p>
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
          <span className="text-[11px] text-muted-foreground">{activity}</span>
          {SHOW_WORKFLOW_RUN_LOGS && stats.totalRuns > 0 && (
            <span className="shrink-0 text-[11px] font-medium text-primary">View logs</span>
          )}
        </div>
      </div>
    );
  };

  // Catalog view
  return (
    <>
    <CopilotPanel
      open={copilotOpen}
      onOpenChange={setCopilotOpen}
      onExpand={() => {
        // Nothing to hand over. Home renders the same AdminChatContext
        // conversation, so this is purely a navigation.
        setCopilotOpen(false);
        onNavigateToSection?.("home");
      }}
      onWorkflowCreated={(workflowId) => {
        // Hand off to the canvas: the assistant's job ends at "draft created".
        setCopilotOpen(false);
        setBuilderId(workflowId);
      }}
    />
    <Tabs
      value={catalogTab}
      onValueChange={(value) => {
        setCatalogTab(value as CatalogTab);
        setCatalogSearch("");
      }}
      className={catalogTab === "capabilities" ? "h-full w-full" : "w-full"}
    >
      {catalogTab !== "capabilities" && !automationsAllowed && (
        <div className="mt-6 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-amber-900/60 dark:bg-amber-950/30">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              <Lock className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">You're in Sandbox mode (S$1/mo)</p>
              <p className="text-xs text-amber-800 dark:text-amber-200/80">
                {hasConnectedChannel
                  ? `Turn on any workflow below to go live on the ${intendedPlanCfg?.name ?? "selected"} plan${
                      upgradePrice ? ` (S$${upgradePrice}/mo)` : ""
                    } and start running it. Import contacts and build customer profiles anytime.`
                  : "Connect a messaging channel first, then turn on any workflow to go live and start your plan."}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="shrink-0 bg-amber-600 text-white hover:bg-amber-700"
            onClick={() => setSearchParams({ tab: "settings", settings: "billing" })}
          >
            View plans
          </Button>
        </div>
      )}

      {catalogTab === "capabilities" ? (
        <WorkflowCapabilitySettings
          // Both canvas workflows are opt-in: no saved row means off.
          isAutomationEnabled={(key) => automationsAllowed && (settings[key] ?? false)}
          onAutomationToggle={toggle}
        />
      ) : workflowCatalogPending ? (
        <WorkflowCatalogLoading />
      ) : workflowCatalogFailed ? (
        <div
          className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 p-5"
          data-testid="workflow-catalog-error"
          role="alert"
        >
          <p className="text-sm font-medium text-foreground">Workflow activity could not be loaded.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your workflows are still safe. Try loading the catalog again.
          </p>
          <Button type="button" variant="outline" size="sm" className="mt-4" onClick={retryWorkflowCatalog}>
            Try again
          </Button>
        </div>
      ) : (
      <div className="mt-6 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_260px]">
        <main data-testid="workflow-sections" className="min-w-0">
          {catalogTab === "my" ? (
            <>
              {visibleCatalogEntries.length > 0 ? (
                <div
                  data-testid="my-workflows-list"
                  className="overflow-hidden rounded-lg border border-border bg-card shadow-sm"
                >
                  {visibleCatalogEntries.map(renderCatalogCard)}
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-dashed border-border bg-card px-6 py-8 text-center">
                  <p className="text-sm font-medium text-foreground">
                    {normalizedCatalogSearch ? "No workflows match your search." : "No workflows yet."}
                  </p>
                  {!normalizedCatalogSearch && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      Create one from a template or start from scratch.
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="neutral-action-button standard-action-button mt-4"
                    onClick={() => {
                      if (normalizedCatalogSearch) {
                        setCatalogSearch("");
                      } else {
                        setCatalogTab("templates");
                      }
                    }}
                  >
                    {normalizedCatalogSearch ? "Clear search" : "Browse templates"}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div data-testid="template-sections">
              {visibleCatalogEntries.length > 0 && (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {visibleCatalogEntries.map(renderCatalogCard)}
                </div>
              )}
              {visibleCatalogEntries.length === 0 && (
                <div className="rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
                  <p className="text-sm font-medium text-foreground">No templates match these filters.</p>
                  <p className="mt-1 text-sm text-muted-foreground">Try another search or clear your filters.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="neutral-action-button standard-action-button mt-4"
                    onClick={clearCatalogFilters}
                  >
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </main>

        {catalogTab === "my" && businessId ? (
          <aside className="xl:sticky xl:top-[116px]" aria-label="Paused customers">
            <OutreachHolds businessId={businessId} />
          </aside>
        ) : catalogTab === "templates" ? (
          <aside className="rounded-lg border border-border bg-card shadow-sm xl:sticky xl:top-[116px]" aria-label="Workflow filters">
            <div className="flex items-center justify-between gap-3 px-4 pt-4">
              <h2 className="font-semibold text-foreground">Filter</h2>
              {(activeCatalogFilterCount > 0 || catalogSearch) && (
                <button
                  type="button"
                  onClick={clearCatalogFilters}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="px-4 pb-4 pt-5">
              <h3 className="text-sm font-medium text-foreground">Business type</h3>
              <p className="mb-3 mt-1 text-xs leading-relaxed text-muted-foreground">
                Workflows applicable to any business remain visible.
              </p>
              <div className="space-y-3">
                {([
                  ["health", "Health & wellness"],
                  ["hospitality", "Hospitality & entertainment"],
                ] as const).map(([value, label]) => (
                  <label key={value} className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
                    <Checkbox
                      className="border-foreground/60 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                      checked={businessTypeFilters.includes(value)}
                      onCheckedChange={() => toggleCatalogFilter(value, businessTypeFilters, setBusinessTypeFilters)}
                      aria-label={label}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t border-border px-4 pb-4 pt-5">
              <h3 className="text-sm font-medium text-foreground">Objective</h3>
              <p className="mb-3 mt-1 text-xs leading-relaxed text-muted-foreground">
                Show workflows that support a specific customer outcome.
              </p>
              <div className="space-y-3">
                {CATALOG_OBJECTIVES.map((objective) => (
                  <label key={objective.id} className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
                    <Checkbox
                      className="border-foreground/60 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                      checked={objectiveFilters.includes(objective.id)}
                      onCheckedChange={() => toggleCatalogFilter(objective.id, objectiveFilters, setObjectiveFilters)}
                      aria-label={objective.title}
                    />
                    <span>{objective.title}</span>
                  </label>
                ))}
              </div>
            </div>
          </aside>
        ) : null}
      </div>
      )}

      <Dialog
        open={Boolean(previewTemplate)}
        onOpenChange={(open) => !open && setPreviewTemplateId(null)}
      >
        <DialogContent
          className={`max-h-[90vh] overflow-y-auto p-0 ${
            previewMode === "capability" ? "max-w-7xl" : "max-w-5xl"
          }`}
        >
          {previewTemplate && previewSteps && (
            <>
              <DialogHeader className="border-b border-border px-6 py-5 pr-12">
                <div className="flex flex-wrap items-center gap-2">
                  <DialogTitle className="font-heading text-xl">{previewTemplate.name}</DialogTitle>
                  {previewMode === "template" && (
                    <Badge
                      variant="outline"
                      className="rounded-sm bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                    >
                      {previewBusinessLabel}
                    </Badge>
                  )}
                </div>
                <DialogDescription className="max-w-3xl leading-relaxed">
                  {previewTemplate.description}
                </DialogDescription>
              </DialogHeader>

              <div className="px-6 pb-5 pt-3">
                <p className="mb-3 text-sm font-heading font-semibold text-foreground">
                  How it works
                </p>
                <StepDiagram
                  key={`${previewMode}-${previewTemplate.id}`}
                  steps={previewSteps}
                  prose={previewTemplate.automation?.prose}
                  initialViewMode={previewMode === "capability" ? "detailed" : "simplified"}
                  showViewSwitcher={previewMode === "template"}
                  editable={previewMode === "capability"}
                  showInsertAffordances={previewMode === "template"}
                  onStepsChange={previewMode === "capability" ? (nextSteps) => {
                    setCapabilityStepOverrides((current) => ({
                      ...current,
                      [previewTemplate.id]: nextSteps,
                    }));
                  } : undefined}
                />
              </div>

              {previewMode === "template" && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
                  <p className="text-xs text-muted-foreground">
                    A copy is created so you can review and edit every step before publishing.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="neutral-action-button shrink-0"
                    disabled={isAllOutlets || duplicatingTemplateId !== null}
                    title={isAllOutlets ? "Select an outlet to use this template" : undefined}
                    onClick={async () => {
                      const created = await duplicateTemplate(
                        previewTemplate.automation?.id ?? previewTemplate.recipeId!,
                      );
                      if (created) setPreviewTemplateId(null);
                    }}
                  >
                    {duplicatingTemplateId === (previewTemplate.automation?.id ?? previewTemplate.recipeId)
                      ? "Creating…"
                      : "Use template"}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={upgrade.open}
        onOpenChange={(o) => !upgrade.busy && setUpgrade((u) => ({ ...u, open: o }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Go live on the {intendedPlanCfg?.name ?? "selected"} plan?</AlertDialogTitle>
            <AlertDialogDescription>
              Turning on a workflow takes your business live. You'll be charged{" "}
              <strong>S${upgradeCharge?.toLocaleString()}/{CYCLE_UNIT[upgradeCycle]}</strong> for the{" "}
              {intendedPlanCfg?.name ?? "selected"} plan now (prorated from your S$1 Sandbox), and your
              workflow automations start running immediately. You can change your plan anytime in Billing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={upgrade.busy}>Not yet</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmUpgrade();
              }}
              disabled={upgrade.busy}
            >
              {upgrade.busy ? "Upgrading…" : `Go live — S$${upgradeCharge?.toLocaleString()}/${CYCLE_UNIT[upgradeCycle]}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
    </>
  );
};

export default Automations;
